import React from 'react';
import { FaSearch } from 'react-icons/fa';
import Button from '~/components/button';
import CardView from '~/components/cartView';
import Checkbox from '~/components/checkbox';
import ContainerFlex from '~/components/containerFlex';
import Grid from '~/components/grid';
import Input, { MaskTypes } from '~/components/input';
import PictureCard from '~/components/pictureCard';
import Select from '~/components/select';
import { setFormState } from '~/utils/form';
import { IValidators } from '~/utils/validators';
import * as St from './ClientSection.styles';

interface ClientSectionProps {
  form: Record<string, any>;
  errors: IValidators;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  handleCEP: () => void | Promise<void>;
  getAddressByCepLoading: boolean;
  handlePictureBiometric: () => string;
  isLoading: boolean;
  disabledButtonSave: () => boolean;
  updateClient: () => Promise<void>;
  cpfMasked: string;
}

const ClientSection: React.FC<ClientSectionProps> = ({
  form,
  errors,
  setForm,
  handleCEP,
  getAddressByCepLoading,
  handlePictureBiometric,
  isLoading,
  disabledButtonSave,
  updateClient,
  cpfMasked,
}) => {
  return (
    <St.SectionContent>
      <ContainerFlex marginLeft={26} marginRight={26}>
        <Grid xs={1} lg={2} gap={30}>
          <ContainerFlex marginTop={36}>
            <ContainerFlex center marginBottom={30}>
              <PictureCard
                width={200}
                height={200}
                showOptions
                src={handlePictureBiometric()}
              />
            </ContainerFlex>

            <Grid xs={2} lg={2} gap={20}>
              <Input
                label="Status do Cliente"
                readOnly
                value={form?.statusCliente.value}
              />
              <Input
                label="Readmitido"
                readOnly
                value={form?.readmissao ? 'SIM' : 'NÃO'}
              />
            </Grid>
            <Input
              label="CPF"
              value={cpfMasked}
              placeholder="000.000.000-00"
              maxLength={14}
              mask={MaskTypes.CPF}
            />
            <Input
              label="Nome completo"
              value={form.nome.value}
              name={form.nome.name}
            />
            <Input
              label="Nome social"
              name={form?.nomeSocial?.name}
              value={form?.nomeSocial?.value}
              onChange={(e) => setFormState(e, form, setForm)}
              errorMessage={errors.socialName}
              edit
            />
            <Input
              label="Telefone"
              name={form.celular.name}
              value={form.celular.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="(00) 00000-0000"
              maxLength={15}
              errorMessage={errors.phone}
              mask={MaskTypes.PHONE}
              edit
            />
            <Input
              label="Segundo Telefone"
              name={form.celular2.name}
              value={form.celular2.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="(00) 00000-0000"
              errorMessage={errors.phone2}
              maxLength={15}
              mask={MaskTypes.PHONE}
              edit
            />
            <Input
              label="Data de nascimento"
              name={form.dataNascimento.name}
              value={form.dataNascimento.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="DD/MM/YYYY"
              maxLength={10}
              mask={MaskTypes.DATE}
              edit
            />
            {form?.dataCancelamento?.value ? (
              <Input
                label="Data de cancelamento"
                name={form?.dataCancelamento.name}
                value={form?.dataCancelamento.value}
                onChange={(e) => setFormState(e, form, setForm)}
                placeholder="DD/MM/YYYY"
                maxLength={10}
                mask={MaskTypes.DATE}
              />
            ) : null}
          </ContainerFlex>

          <ContainerFlex>
            <Input
              label="E-mail"
              value={form.email.value?.trim()}
              name={form.email.name}
              onChange={(e) => setFormState(e, form, setForm)}
              errorMessage={errors.email}
              edit
            />
            <Input label="Empresa" value={form.lojaNome.value} edit />

            <Select
              label="Sexo"
              name={form.genero.name}
              value={form.genero.value}
              options={[
                { id: 'M', value: 'Masculino' },
                { id: 'F', value: 'Feminino' },
              ]}
              onChange={(e) => {
                setFormState(
                  { target: { name: form.genero.name, value: e } },
                  form,
                  setForm,
                );
              }}
            />
            <ContainerFlex
              marginTop={10}
              row
              gap={'10'}
              align="center"
              justifyContent="center"
            >
              <ContainerFlex>
                <Input
                  label="CEP"
                  name={form.endCep.name}
                  value={form.endCep.value}
                  onChange={(e) => setFormState(e, form, setForm)}
                  placeholder="00000-000"
                  width="70%"
                  maxLength={9}
                  mask={MaskTypes.CEP}
                  edit
                />
              </ContainerFlex>

              <Button
                onClick={handleCEP}
                loading={getAddressByCepLoading}
                style={{ gap: 10 }}
              >
                <FaSearch style={{ marginRight: 10 }} />
                <span>Buscar CEP</span>
              </Button>
            </ContainerFlex>
            <Input
              label="Endereço"
              name={form.endLogradouro.name}
              value={form.endLogradouro.value}
              onChange={(e) => setFormState(e, form, setForm)}
              edit
            />

            <div>
              <Grid xs={1} sm={2} gap={10}>
                <ContainerFlex>
                  <Input
                    label="Número"
                    name={form.endNumero.name}
                    value={form.endNumero.value}
                    onChange={(e) => setFormState(e, form, setForm)}
                    edit
                  />
                </ContainerFlex>

                <ContainerFlex>
                  <Input
                    label="Complemento"
                    name={form.endComplemento.name}
                    value={form.endComplemento.value}
                    onChange={(e) => setFormState(e, form, setForm)}
                    edit
                  />
                </ContainerFlex>
              </Grid>

              <Grid xs={1} sm={3} gap={10}>
                <ContainerFlex>
                  <Input
                    label="Bairro"
                    name={form.endBairro.name}
                    value={form.endBairro.value}
                    onChange={(e) => setFormState(e, form, setForm)}
                    readOnly
                  />
                </ContainerFlex>

                <ContainerFlex>
                  <Input
                    label="Cidade"
                    name={form.endCidade.name}
                    value={form.endCidade.value}
                    onChange={(e) => setFormState(e, form, setForm)}
                    readOnly
                  />
                </ContainerFlex>

                <ContainerFlex>
                  <Input
                    label="UF/Estado"
                    name={form.endUf.name}
                    value={form.endUf.value}
                    onChange={(e) => setFormState(e, form, setForm)}
                    readOnly
                  />
                </ContainerFlex>
              </Grid>
            </div>

            <Input
              label="ID do cliente"
              value={form.idClienteAutorizador.value}
            />

            <div style={{ marginBottom: '10px', marginTop: '15px' }}>
              <Checkbox
                value={form.aposentadoPensionista}
                readonly
                message="Aposentado ou Pensionista."
              />
            </div>

            <Input
              label="Profissão"
              edit
              name={form.profissao.name}
              value={form.profissao.value}
              onChange={(e) => setFormState(e, form, setForm)}
            />

            <Input
              label="Nome da Empresa/Órgão onde trabalha"
              edit
              name={form.nomeEmpresa.name}
              value={form.nomeEmpresa.value}
              onChange={(e) => setFormState(e, form, setForm)}
            />

            <Input
              label="Renda estimada"
              name={form.rendaInformada.name}
              value={form.rendaInformada.value}
              onChange={(e) => setFormState(e, form, setForm)}
              edit
              mask={MaskTypes.CURRENCY}
            />
          </ContainerFlex>
        </Grid>
      </ContainerFlex>

      <CardView marginBottom={40} marginTop={20}>
        <ContainerFlex row end padding={24}>
          <Button
            loading={isLoading}
            marginLeft={16}
            onClick={updateClient}
            fitContent
            disabled={disabledButtonSave()}
          >
            Salvar
          </Button>
        </ContainerFlex>
      </CardView>
    </St.SectionContent>
  );
};

export default ClientSection;
